import { css, styled } from "@mui/material";
import { Eye, EyeOff, Folder } from "@procore/core-icons";
import { Box, Button, colors, spacing, Typography } from "@procore/core-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { hiddenActions, useDispatchHidden, useHidden } from "contexts/hidden";
import { highlightActions, useDispatchHighlighted, useHighlighted } from "contexts/highlighted";
import type { TreeNodeRendererProps } from "features/modelTree/types";
import { renderActions, selectMainObject } from "features/render";
import { undoRedoActions } from "features/undoRedo";
import { useTouchLongPress } from "hooks/useTouchLongPress";
import { selectIsProcoreDesign } from "slices/explorer";
import {
    hideObjectHierarchy,
    hideObjectHierarchyFromFilteredTree,
    selectObjectHierarchy,
    selectObjectHierarchyFromFilteredTree,
    showObjectHierarchy,
    showObjectHierarchyFromFilteredTree,
    SYNTHETIC_NODE_ID,
    unselectObjectHierarchy,
    unselectObjectHierarchyFromFilteredTree,
} from "utils/objectActions";
import { getParentPaths } from "utils/objectPath";
import { highlightText } from "utils/textHighlight";
import { getGroupStateForFilteredTree, getGroupStateForIdSet, type GroupState } from "utils/treeHelpers";
import { restoreUndoIds, snapshotUndoIds } from "utils/undoIds";

import { CLICK_DELAY, ITEM_HEIGHT, ROW_PADDING_LEFT } from "../constants";
import { invertGroupState, useIsolationVisibility } from "../hooks/useIsolationVisibility";
import { calculateTreeIndentation, formatNodeDisplayName, getGroupCountLabel } from "../utils/formatting";
import { TreeNodeCheckbox } from "./TreeNodeCheckbox";
import { TreeNodeChevron } from "./TreeNodeChevron";
import { VisibilityButton } from "./VisibilityButton";

// Light grey hint dot shown when only some of a node's children are hidden (from design spec).
const HINT_DOT_COLOR = "#ACB5B9";

const TreeNodeRow = styled(Box)(
    () => css`
        display: flex;
        align-items: center;
        padding-left: ${ROW_PADDING_LEFT}px;
        cursor: pointer;
        transition: background-color 0.15s ease;
        background-color: transparent;
        min-width: 100%;
        width: max-content;
        height: ${ITEM_HEIGHT}px;
        user-select: none;
        gap: ${spacing.sm}px;

        &:hover {
            background-color: ${colors.gray96};
        }
    `,
);

export function ObjectTreeNodeProcore({
    item,
    onToggleExpanded,
    onExpandGroup,
    onSelectInTree,
    setLoading,
    abortSignal,
    tree,
    filteredObjectIds,
    searchTerm,
    onNodeRightClick,
}: TreeNodeRendererProps) {
    const { t } = useTranslation();
    const { node, level } = item;
    const singleClickTimerRef = useRef<number | null>(null);

    const dispatch = useAppDispatch();
    const mainObject = useAppSelector(selectMainObject);
    const mainObjectRef = useRef(mainObject);
    mainObjectRef.current = mainObject;
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const {
        state: { db },
    } = useExplorerGlobals(true);

    const highlightedSet = useHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const dispatchHidden = useDispatchHidden();
    const highlightedIds = highlightedSet.ids;

    const hidden = useHidden();
    const hiddenIds = hidden.ids;
    const isolation = useIsolationVisibility();
    const isolationVisibleIds = isolation.visibleIds;
    const [isHovered, setIsHovered] = useState(false);

    const longPress = useTouchLongPress({
        onLongPress: ({ clientX, clientY }) => {
            if (singleClickTimerRef.current !== null) {
                window.clearTimeout(singleClickTimerRef.current);
                singleClickTimerRef.current = null;
            }
            onNodeRightClick?.(clientX, clientY, item);
        },
    });

    const selected: GroupState = useMemo(() => {
        if (filteredObjectIds != null && tree) {
            return getGroupStateForFilteredTree(highlightedIds, item, filteredObjectIds, tree);
        }
        return getGroupStateForIdSet(highlightedIds, item, tree);
    }, [item, highlightedIds, filteredObjectIds, tree]);

    const hiddenState: GroupState = useMemo(() => {
        const ids = isolationVisibleIds ?? hiddenIds;
        const state =
            filteredObjectIds != null && tree
                ? getGroupStateForFilteredTree(ids, item, filteredObjectIds, tree)
                : getGroupStateForIdSet(ids, item, tree);
        if (isolationVisibleIds) {
            return invertGroupState(state);
        }
        return state;
    }, [item, hiddenIds, isolationVisibleIds, filteredObjectIds, tree]);

    // Show EyeOff:
    // - For leaf rows: when the leaf itself is hidden.
    // - For folder rows: when the row's whole (loaded) subtree is hidden
    //   (hiddenState === "all"), so the hidden state propagates up single-child
    //   chains and stops at the first ancestor with only partially hidden
    //   children — that ancestor shows the hint dot instead.
    //
    // A folder's own id being in the hidden set is deliberately *not* sufficient:
    // folder ids carry no renderable geometry, so a bare container id (such as one
    // resolved from a viewpoint's visibility exceptions, see BM-10786) would show a
    // false EyeOff over a fully visible branch. Fall back on evidence that the
    // subtree really is hidden, which also keeps the EyeOff stable while a folder's
    // children are still loading (incomplete counts never report "all"):
    //   - The row's resolved subtree (`descendants`, cached when hiding a hierarchy)
    //     is fully covered by the hidden set. This also covers synthetic ancestor
    //     rows of the search tree, which share the fake id -1 that never enters the
    //     hidden set.
    //   - Otherwise, an ancestor row is itself in the hidden set. That only happens
    //     when the ancestor was explicitly hidden, since hiding writes the folder id
    //     together with its full real subtree, so this row is hidden by inheritance.
    const isHidden = useMemo(() => {
        if (hiddenState === "all") {
            return true;
        }
        if (isolationVisibleIds) {
            return false;
        }
        if (node.isLeaf) {
            return false;
        }
        const object = node.data.object;
        const descendants = object.descendants;
        if (descendants != null && descendants.length > 0) {
            return descendants.every((id) => hiddenIds.has(id));
        }
        if (object.id === SYNTHETIC_NODE_ID || !hiddenIds.has(object.id)) {
            return false;
        }
        for (const parentPath of getParentPaths(node.path, false)) {
            const ancestorId = tree?.[parentPath]?.data.object.id;
            if (ancestorId !== undefined && ancestorId !== SYNTHETIC_NODE_ID && hiddenIds.has(ancestorId)) {
                return true;
            }
        }
        return false;
    }, [node, hiddenState, hiddenIds, isolationVisibleIds, tree]);

    useEffect(() => {
        return () => {
            if (singleClickTimerRef.current) {
                window.clearTimeout(singleClickTimerRef.current);
                singleClickTimerRef.current = null;
            }
        };
    }, []);

    const handleToggleSelection = useCallback(async () => {
        if (abortSignal.aborted) {
            return;
        }

        const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

        try {
            let affectedIds: number[];
            const isUnselecting = selected === "all";

            // If we have filtered IDs, use the filtered-tree-aware selection functions. These
            // resolve the same real hierarchy as the eye icon's hide/show (including descendants
            // that didn't match the search themselves, e.g. objects with no geometry) so the
            // node and its descendants stay consistent — avoiding the issue where the node is
            // selected but its descendants are not, leading to inconsistent behavior for
            // features like isolate and isolate in X-Ray mode.
            if (filteredObjectIds != null && tree) {
                if (isUnselecting) {
                    affectedIds = await unselectObjectHierarchyFromFilteredTree({
                        nodes: objects,
                        filteredObjectIds,
                        db,
                        abortSignal,
                        setLoading,
                        dispatchHighlighted,
                    });
                } else {
                    affectedIds = await selectObjectHierarchyFromFilteredTree({
                        nodes: objects,
                        filteredObjectIds,
                        db,
                        abortSignal,
                        setLoading,
                        dispatchHighlighted,
                    });
                }
            } else if (isUnselecting) {
                affectedIds = await unselectObjectHierarchy({
                    nodes: objects,
                    db,
                    abortSignal,
                    setLoading,
                    dispatchHighlighted,
                });
            } else {
                // Note: dispatchHidden is intentionally NOT passed here.
                // Objects can exist in both selected and hidden sets simultaneously.
                affectedIds = await selectObjectHierarchy({
                    nodes: objects,
                    db,
                    abortSignal,
                    setLoading,
                    dispatchHighlighted,
                });
            }
            if (affectedIds.length > 0) {
                const currentMainObject = mainObjectRef.current;
                const clearedMainObjectId =
                    isUnselecting && currentMainObject !== undefined && affectedIds.includes(currentMainObject)
                        ? currentMainObject
                        : undefined;

                // On select, mark the clicked node as the new main object so that the
                // properties panel and headers populate, mirroring canvas pick behaviour.
                const newMainObjectId = !isUnselecting ? item.node.data.object.id : undefined;
                const previousMainObject = !isUnselecting ? currentMainObject : undefined;

                if (clearedMainObjectId !== undefined) {
                    // Selection initiated in the tree — don't let it auto-scroll the row.
                    onSelectInTree?.(undefined);
                    dispatch(renderActions.setMainObject(undefined));
                } else if (newMainObjectId !== undefined) {
                    // Selection initiated in the tree — don't let it auto-scroll the row.
                    onSelectInTree?.(newMainObjectId);
                    dispatch(renderActions.setMainObject(newMainObjectId));
                }

                const shrunkAffected = snapshotUndoIds(affectedIds);

                dispatch(
                    undoRedoActions.push({
                        description: isUnselecting ? "Unselect objects" : "Select objects",
                        undoCallback: () => {
                            const ids = restoreUndoIds(shrunkAffected);
                            dispatchHighlighted(
                                isUnselecting ? highlightActions.add(ids) : highlightActions.remove(ids),
                            );
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(clearedMainObjectId));
                            } else if (newMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(previousMainObject));
                            }
                        },
                        redoCallback: () => {
                            const ids = restoreUndoIds(shrunkAffected);
                            dispatchHighlighted(
                                isUnselecting ? highlightActions.remove(ids) : highlightActions.add(ids),
                            );
                            if (clearedMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(undefined));
                            } else if (newMainObjectId !== undefined) {
                                dispatch(renderActions.setMainObject(newMainObjectId));
                            }
                        },
                    }),
                );
            }
        } catch (error) {
            if (!abortSignal.aborted) {
                console.error("Selection failed:", error);
            }
        }
    }, [
        item,
        selected,
        db,
        abortSignal,
        setLoading,
        dispatch,
        dispatchHighlighted,
        filteredObjectIds,
        tree,
        onSelectInTree,
    ]);

    const handleRowClick = () => {
        if (longPress.wasLongPressTriggered()) {
            return;
        }

        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }

        singleClickTimerRef.current = window.setTimeout(() => {
            singleClickTimerRef.current = null;
            handleToggleSelection();
        }, CLICK_DELAY);
    };

    const handleRowDoubleClick = () => {
        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }

        if (!node.isLeaf) {
            onToggleExpanded(item);
        }
    };

    const handleToggleVisibility = useCallback(
        async (e: React.MouseEvent) => {
            e.stopPropagation();

            const objects = item.nodeGroup?.map((n) => n.data.object) ?? [item.node.data.object];

            // Toggle direction mirrors the icon: a row showing EyeOff toggles back to visible.
            const shouldShow = isHidden;

            try {
                let affectedIds: number[];
                if (isolationVisibleIds) {
                    affectedIds = await isolation.setHierarchyVisible({
                        nodes: objects,
                        filteredObjectIds,
                        visible: shouldShow,
                        db,
                        abortSignal,
                        setLoading,
                    });
                    return;
                }
                // If we have filtered IDs, use the filtered-tree-aware visibility functions
                if (filteredObjectIds != null && tree) {
                    if (shouldShow) {
                        affectedIds = await showObjectHierarchyFromFilteredTree({
                            nodes: objects,
                            filteredObjectIds,
                            dispatchHidden,
                            db,
                            abortSignal,
                            setLoading,
                        });
                    } else {
                        affectedIds = await hideObjectHierarchyFromFilteredTree({
                            nodes: objects,
                            filteredObjectIds,
                            dispatchHidden,
                            db,
                            abortSignal,
                            setLoading,
                        });
                    }
                } else {
                    if (shouldShow) {
                        affectedIds = await showObjectHierarchy({
                            nodes: objects,
                            db,
                            abortSignal,
                            setLoading,
                            dispatchHidden,
                        });
                    } else {
                        affectedIds = await hideObjectHierarchy({
                            nodes: objects,
                            db,
                            abortSignal,
                            setLoading,
                            dispatchHidden,
                        });
                    }
                }
                if (affectedIds.length > 0) {
                    if (shouldShow) {
                        // A now-visible descendant invalidates any ancestor's "explicitly
                        // hidden" claim: the ancestor's own id in the hidden set is what keeps
                        // its row on the persistent EyeOff, so it must be dropped for the
                        // partial-hidden hint dot to take over. Explicitly hiding a folder
                        // writes its own id (see hideObjectHierarchy), but unhiding a
                        // descendant only removes that descendant's subtree.
                        const staleAncestorIds: number[] = [];
                        for (const parentPath of getParentPaths(node.path, false)) {
                            const ancestorId = tree?.[parentPath]?.data.object.id;
                            if (
                                ancestorId !== undefined &&
                                ancestorId !== SYNTHETIC_NODE_ID &&
                                hiddenIds.has(ancestorId)
                            ) {
                                staleAncestorIds.push(ancestorId);
                            }
                        }
                        if (staleAncestorIds.length > 0) {
                            dispatchHidden(hiddenActions.remove(staleAncestorIds));
                            affectedIds = [...affectedIds, ...staleAncestorIds];
                        }
                    }
                    const shrunkAffected = snapshotUndoIds(affectedIds);
                    dispatch(
                        undoRedoActions.push({
                            description: shouldShow ? "Show objects" : "Hide objects",
                            undoCallback: () => {
                                const ids = restoreUndoIds(shrunkAffected);
                                dispatchHidden(shouldShow ? hiddenActions.add(ids) : hiddenActions.remove(ids));
                            },
                            redoCallback: () => {
                                const ids = restoreUndoIds(shrunkAffected);
                                dispatchHidden(shouldShow ? hiddenActions.remove(ids) : hiddenActions.add(ids));
                            },
                        }),
                    );
                }
            } catch (error) {
                if (!abortSignal.aborted) {
                    console.error("Toggle visibility failed:", error);
                }
            }
        },
        [
            item,
            node,
            isHidden,
            hiddenIds,
            db,
            abortSignal,
            setLoading,
            dispatch,
            dispatchHidden,
            isolation,
            isolationVisibleIds,
            filteredObjectIds,
            tree,
        ],
    );

    const handleContextMenu = (event: React.MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        longPress.cancel();

        if (longPress.wasLongPressTriggered()) {
            return;
        }

        const mouseX = event.clientX;
        const mouseY = event.clientY;

        if (singleClickTimerRef.current !== null) {
            window.clearTimeout(singleClickTimerRef.current);
            singleClickTimerRef.current = null;
        }

        onNodeRightClick?.(mouseX, mouseY, item);
    };

    // Only some of the node's children are hidden (partial state). Never true for plain
    // leaf rows — the group state helpers only return "some" for folders and node groups.
    const hasPartiallyHiddenChildren = !isHidden && hiddenState === "some";

    const levelIndent = calculateTreeIndentation(level);
    const displayName = formatNodeDisplayName(item);
    const groupCountLabel = getGroupCountLabel(item);

    const hasChildren = !node.isLeaf;
    const isExpanded = node.expanded;

    return (
        <TreeNodeRow
            data-testid="object-tree-node-row"
            {...(isProcoreDesign ? {} : longPress.handlers)}
            onClick={handleRowClick}
            onDoubleClick={handleRowDoubleClick}
            onContextMenu={handleContextMenu}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <TreeNodeCheckbox selected={selected} onSelection={handleToggleSelection} />
            {level > 0 && <Box style={{ width: `${levelIndent}px`, flexShrink: 0 }} />}

            {hasChildren && (
                <>
                    <TreeNodeChevron isExpanded={isExpanded} onToggle={() => onToggleExpanded(item)} />
                    <Box display="flex">
                        <Folder style={{ color: colors.black, width: 16, height: 16 }} />
                    </Box>
                </>
            )}

            <Typography
                style={{
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                }}
            >
                {searchTerm ? highlightText(displayName, searchTerm) : displayName}
            </Typography>
            {item.nodeGroup && (
                <Button
                    variant="tertiary"
                    size="sm"
                    onClick={(e) => {
                        e.stopPropagation();
                        onExpandGroup?.(item);
                    }}
                >
                    {groupCountLabel}
                </Button>
            )}
            {node.loading && <Typography style={{ fontSize: "11px", color: colors.gray60 }}>{t("loading")}</Typography>}

            {!isHovered && !isHidden && hasPartiallyHiddenChildren && (
                <Box
                    data-testid="object-tree-node-hint-dot"
                    style={{
                        marginLeft: "auto",
                        position: "sticky",
                        right: 0,
                        backgroundColor: colors.white,
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        // Same footprint as the visibility button (16px icon + 4px padding)
                        // so the dot and the eye icon occupy the same spot.
                        width: 24,
                        height: 24,
                    }}
                >
                    <svg width="7" height="7" viewBox="0 0 7 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="3.5" cy="3.5" r="3.5" fill={HINT_DOT_COLOR} />
                    </svg>
                </Box>
            )}

            {(isHovered || isHidden) && (
                <VisibilityButton
                    data-testid="object-tree-node-visibility"
                    style={{
                        marginLeft: "auto",
                        position: "sticky",
                        right: 0,
                        backgroundColor: colors.white,
                        flexShrink: 0,
                    }}
                    onClick={handleToggleVisibility}
                >
                    {isHidden ? (
                        <EyeOff style={{ width: 16, height: 16 }} />
                    ) : (
                        <Eye style={{ width: 16, height: 16 }} />
                    )}
                </VisibilityButton>
            )}
        </TreeNodeRow>
    );
}
